import { Text, Box, Flex, Button } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import { useBulkUnmapADPWFNJobs } from 'hooks/integrations/useBulkUnmapADPJobs';
import { fetchAdpHomeCostCodes } from 'hooks/integrations/useFetchAdpHomeCostCodes';
import { useFetchADPTimeeroPaginatedTasks } from 'hooks/integrations/useFetchADPTimeeroPaginatedTasks';
import { useFetchIntegrationSyncStatusById } from 'hooks/integrations/useFetchIntegrationSyncStatusById';
import { IAdpHomeCostGroupsSearchQuery } from 'hooks/integrations/useFetchPaginatedAdpHomeCostGroups';
import { IMapADPHomeCost, useMapADPHomeCost } from 'hooks/integrations/useMapADPHomeCost';
import { useMapBulkADPWFNJobs } from 'hooks/integrations/useMapBulkADPWFNJobs';
import { useSyncADPWorker } from 'hooks/integrations/useSyncADPWorker';
import { IUnmapADPHomeCost, useUnmapADPHomeCost } from 'hooks/integrations/useUnmapADPHomeCost';
import { getAsyncSelectLoadOptionsNoPaginate } from 'utils/helpers';
import toaster from 'utils/toaster';

import HomeCostTableFooter from '../CostCodesTableFooter';

const initialSearchQuery: IAdpHomeCostGroupsSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const TaskCodesMapping = () => {
  const { t } = useTranslation(['common', 'integrations']);

  const syncLongPollingStatus = useRef<'idle' | 'fetching'>('idle');

  const [mappedTasks, setMappedTasks] = useState<any>(null);
  const [currentMappedTask, setCurrentMappedTask] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IAdpHomeCostGroupsSearchQuery>(initialSearchQuery);

  const {
    data: timeeroTasksData,
    refetch,
    status: adpTimeeroTasksStatus,
  } = useFetchADPTimeeroPaginatedTasks(searchQuery);

  const { status: adpHomeCostMapStatus, mutateAsync: adpHomeCostMapMutateAsync } = useMapADPHomeCost();
  const { status: adpHomeCostUnmapStatus, mutateAsync: adpHomeCostUnmapMutateAsync } = useUnmapADPHomeCost();

  const { status: adpJobsBulkUnmapStatus, mutateAsync: adpJobsBulkUnmapMutatAsync } = useBulkUnmapADPWFNJobs();
  const { status: adpHomeCostBulkStatus, mutateAsync: adpHomeCostBulkMutateAsync } = useMapBulkADPWFNJobs();

  const { status: adpSyncStatus, mutateAsync: adpSyncMutateAsync, data: syncADPData } = useSyncADPWorker();

  useFetchIntegrationSyncStatusById(syncADPData?.payload?.id, {
    enabled: syncLongPollingStatus.current === 'fetching' && syncADPData?.payload?.id ? true : false,
    refetchInterval: (response: any) => {
      if (response?.payload?.finished === true) {
        if (syncLongPollingStatus.current === 'fetching') {
          refetch();
          toaster.success({ description: response?.message || 'Synced Successfully.' });
        }
        syncLongPollingStatus.current = 'idle';
        return false;
      }

      return 3000;
    },
  });

  const columns = [
    {
      id: t('common:selection'),
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    {
      Header: t('common:tasks'),
      accessor: 'name',
      Cell: (data: CellValue) => {
        return (
          <Text textOverflow="ellipsis" overflow="hidden">
            {data?.value}
          </Text>
        );
      },
    },
    {
      Header: t('integrations:adp.homeCost'),
      accessor: 'homeCost',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <Box position="relative" width="100%">
            <ReactSelectAsyncPaginate
              loadOptions={adpGroupLoadOptions}
              additional={{
                page: 1,
              }}
              value={
                data.row.original?.adp_cost_code
                  ? data.row.original?.adp_cost_code || null
                  : mappedTasks &&
                      mappedTasks[`${data.row.original.id}`] &&
                      mappedTasks[`${data.row.original.id}`].adp_cost_code
                    ? mappedTasks[`${data.row.original.id}`].adp_cost_code
                    : null
              }
              onChange={(value: any) => {
                setMappedTasks((prevValues: any) => {
                  return {
                    ...prevValues,
                    [data.row.original.id]: {
                      adp_cost_code: value || null,
                    },
                  };
                });
              }}
              size="md"
              variant="filled"
              fontSize="sm"
              width="100%"
              debounceTimeout={300}
              menuPortalTarget={document.body}
              isSearchable
              placeholder="Select Cost Code"
              getOptionValue={(option: any) => option.id}
              getOptionLabel={(option: any) => {
                return option?.cost_code;
              }}
            />
          </Box>
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 120,
      Cell: (data: CellValue) =>
        data.row.original.adp_cost_code ? (
          <React.Fragment>
            <Button
              _hover={{ bg: 'secondary.100' }}
              _active={{ bg: 'secondary.100' }}
              fontWeight="bold"
              background="rgba(250, 85, 85, 0.2)"
              color="rgba(250, 85, 85, 1)"
              isLoading={adpHomeCostUnmapStatus === 'loading' && currentMappedTask === data.row.original.id}
              onClick={() => unmapHomeCost(data.row.original?.id, data.row.original?.adp_cost_code?.id)}
            >
              {t('integrations:adp.removeCostCode')}
            </Button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button
              _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
              _active={{ bg: 'rgba(255, 103, 32, 0.2)' }}
              background="rgba(255, 103, 32, 0.1)"
              colorScheme="rgba(255, 103, 32, 0.3)"
              color="secondary.500"
              isLoading={adpHomeCostMapStatus === 'loading' && currentMappedTask === data.row.original.id}
              onClick={() => mapHomeCost(data.row.original?.id)}
            >
              {t('integrations:adp.mapCostCode')}
            </Button>
          </React.Fragment>
        ),
    },
  ];

  const mapHomeCost = (taskId: number) => {
    if (mappedTasks && mappedTasks[taskId] && mappedTasks[taskId].hasOwnProperty('adp_cost_code')) {
      const postData: IMapADPHomeCost = {
        job_id: taskId,
        cost_code_id: mappedTasks[taskId]?.adp_cost_code?.id,
      };

      setCurrentMappedTask(taskId);

      adpHomeCostMapMutateAsync(postData).then(() => {
        refetch();
        setCurrentMappedTask(null);
      });
    }
  };

  const unmapHomeCost = (taskId: number, costCodeId: number) => {
    setCurrentMappedTask(taskId);

    const postData: IUnmapADPHomeCost = {
      job_id: taskId,
      cost_code_id: costCodeId,
    };

    adpHomeCostUnmapMutateAsync(postData).then(() => {
      setCurrentMappedTask(null);
      refetch();
      setMappedTasks((prevValue: any) => ({
        ...prevValue,
        [taskId]: null,
      }));
    });
  };

  const handleBulkMapping = (selectedFlatRows: any[]) => {
    const mappedData: Array<{ job_id: number; cost_code_id: number }> = [];

    selectedFlatRows.forEach(selectedRow => {
      if (mappedTasks && mappedTasks[selectedRow.original.id]) {
        mappedData.push({
          cost_code_id: mappedTasks[selectedRow.original.id]?.adp_cost_code?.id,
          job_id: selectedRow?.original?.id,
        });
      }
    });

    const postData = {
      mappings: mappedData || [],
    };

    adpHomeCostBulkMutateAsync(postData).then(() => {
      refetch();
      setMappedTasks(null);
    });
  };

  const handleBulkUnmapping = (selectedFlatRows: any[]) => {
    const mappedData: Array<{ job_id: number; cost_code_id: number }> = [];

    selectedFlatRows.forEach(selectedRow => {
      if (selectedRow?.original?.adp_cost_code) {
        mappedData.push({
          cost_code_id: selectedRow?.original?.adp_cost_code?.id,
          job_id: selectedRow?.original?.id,
        });
      }
    });

    const postData = {
      mappings: mappedData || [],
    };

    adpJobsBulkUnmapMutatAsync(postData as any).then(() => {
      refetch();
      setMappedTasks(null);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue: string = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IAdpHomeCostGroupsSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const adpGroupLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptionsNoPaginate(fetchAdpHomeCostCodes, { page, search });
  };

  const onSyncADP = () => {
    adpSyncMutateAsync('codes');
    syncLongPollingStatus.current = 'fetching';
  };

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center">
        <Text variant="h2" fontSize="lg" ml={10} textAlign="center">
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={adpSyncStatus === 'loading' || syncLongPollingStatus.current === 'fetching'}
          onClick={onSyncADP}
        >
          {t('integrations:adp.syncChangesFromAdp')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} w="100%">
        <SearchField
          inputProps={{
            placeholder: t('integrations:adp.searchByTaskName'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>
      <Table
        columns={columns}
        data={timeeroTasksData?.payload || []}
        isLoading={adpTimeeroTasksStatus === 'loading'}
        enableRowSelect
        paginationData={timeeroTasksData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <HomeCostTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkMapping={handleBulkMapping}
              handleBulkUnmapping={handleBulkUnmapping}
              mappedTasks={mappedTasks}
              isBulkMapLoading={adpHomeCostBulkStatus === 'loading'}
              isBulkUnmapLoading={adpJobsBulkUnmapStatus === 'loading'}
            />
          );
        }}
      />
    </Box>
  );
};

export default TaskCodesMapping;
